import * as React from 'react';
import { Input, Tooltip, TreeSelect } from 'antd';
import { DataNode } from 'antd/lib/tree';
import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';

// import { Tree } from 'antd/es/tree';
// import { TreeNodeNormal } from 'antd/lib/tree-select/interface';
import InputControlProps from '../../../common/InputControlProps';

// export type TreeData = TreeDataNode;
export type TreeData = {
  title?: React.ReactNode | ((data: DataNode) => React.ReactNode);
  key: string;
  value: string;
  children?: Array<TreeData> | undefined;
  selectable?: boolean;
};

export interface SearchableTreeData extends TreeData {
  search: string;
}

interface CommonTreeSelectProps extends InputControlProps {
  /**
   * Callback that should return TreeData list, valid for TreeSelect component.
   * Note that it should be more or less fast because it is is called on
   * every render in CommonTreeSelect component and will be called on
   * every update of changing filtering search string.
   * Note that hierarchy can be deep.
   *
   * @param { string } search - filtering string to filter our TreeData list and to
   * display only part of it, depending on what user wrote in search box
   *
   * @return { TreeData[] } list
   */
  getTreeData: (search: string[] | string) => TreeData[];

  /**
   * This method is necessary for CommonTreeSelect only for single case and multiple
   * case when search string is presented.
   * Problem: our api eats id of city (for example) but TreeSelect will return
   * in value - country id if we click on country. Same bug when we type search
   * and then select country - we will get country code instead of list of cities.
   * How to resolve? Write method that will return children for case if input is parent
   *
   * @param { string } value - selected value from TreeData, potential parent
   * @param { string } search - filtering string to filter our TreeData list and to
   * display only part of it, depending on what user wrote in search box
   *
   * @return { string[] } proper values list from tree data, can not include parents,
   * only last level children
   */
  getValuesForSingleParentCase: (value: string, search: string) => string[];

  allValues: SearchableTreeData[];
}

export class CommonTreeSelect extends React.Component<
  CommonTreeSelectProps,
  { search: string; manualInputVisible: boolean; manualSearch: string }
> {
  static defaultProps = {
    multiple: false,
    placeholder: 'Please select',
  };

  state = {
    search: '',
    manualInputVisible: true,
    value: null,
    manualSearch: '',
  };

  componentDidMount() {
    this._setManualSearchValue(this.props.value);
  }

  UNSAFE_componentWillReceiveProps({ value }: CommonTreeSelectProps) {
    if (!value || (!value.length && this.props.value !== value)) {
      this._setManualSearchValue([]);
    }
  }

  _setManualSearchValue(values) {
    if (this.props.showManualSearch) {
      this.setState({
        manualSearch: (values || []).join(', '),
      });
    }
  }

  onChange = (values: string | string[] | any): void => {
    const multiple: boolean = this.props.multiple!;
    const search: string = this.state.search;

    const singleCase: boolean = !multiple && values;
    const multipleSingleParentCase: string | boolean =
      multiple && search && values.length === 1;

    if (multipleSingleParentCase) {
      values = values[0];
    }

    if (singleCase || multipleSingleParentCase) {
      values = this.props.getValuesForSingleParentCase(values, search);
    }

    if (singleCase) {
      values = values[0];
    }

    this.props.onChange(values);
    this._setManualSearchValue(values);
    this.onSearch('');
  };

  onSearch = (search: string): void => {
    this.setState({ search });
  };

  onCustomChanged = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const manualSearch = e.target.value;
    this.setState({ manualSearch }, () => {
      const groupedValues = groupBy(this.props.allValues, 'value');

      this.props.onChange(
        uniq(
          manualSearch
            // replacing all spaces and brackets from input, in case if use
            // passed array from json response like ["1","2"] will become 1,2
            // eslint-disable-next-line no-useless-escape
            .replace(/[ \[]'"/g, '')
            // in case if user chosen ; instead of , to separate elements - replacing them to simple commas
            .replace(/;/g, '')
            .split(',')
            .filter((value: string) => groupedValues[value]),
        ),
      );
    });
  };

  render() {
    const placeholder: string | undefined = this.props.placeholder;
    let value: string | string[] | undefined = this.props.value;
    if (!this.props.multiple) {
      // this equality to undefined for falsy values is required for non multiple case
      // otherwise label in TreeSelect is not displayed at all and looks confusing
      value = this.props.value || undefined;
    }

    return (
      <div className={this.props.className} style={{ display: 'flex' }}>
        <TreeSelect
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          treeData={this.props.getTreeData(this.state.search)}
          placeholder={placeholder}
          // searchPlaceholder={placeholder}
          filterTreeNode={() => true}
          style={{ width: '100%', height: `100%` }}
          onChange={this.onChange}
          onSearch={this.onSearch}
          treeCheckable={this.props.multiple}
          showSearch={true}
          allowClear={true}
          multiple={this.props.multiple}
          value={value}
          disabled={this.props.disabled}
          popupMatchSelectWidth={this.props.popupMatchSelectWidth}
        />

        {this.props.showManualSearch && (
          <Tooltip
            placement="top"
            title={`You can paste here comma separated ids, or
              comma separated ids with quotes and values will be parsed.
              Example: 1,2,3,4,5,6,31,82 or "1", "2", "3", or even ["1", "2"]`}
          >
            <Input.TextArea
              style={{ width: '200px' }}
              rows={1}
              onChange={this.onCustomChanged}
              value={this.state.manualSearch}
              placeholder="... or type manually"
            />
          </Tooltip>
        )}
      </div>
    );
  }
}

export default CommonTreeSelect;
